import {
  BraintrustEnvironment,
  useCreateBraintrustDatasetMutation,
  useSaveConversationSnapshotToDatasetMutation,
} from '@shared/generated/graphql';

export type NewDatasetInput = {
  name: string;
  environment: BraintrustEnvironment;
};

export type SaveSnapshotArgs = {
  conversationId: string;
  timestamp: Date;
  existingDatasetIds: string[];
  newDatasets: NewDatasetInput[];
};

export function useSaveSnapshotToDataset() {
  const [createDatasetMutation] = useCreateBraintrustDatasetMutation();
  const [saveSnapshotMutation] = useSaveConversationSnapshotToDatasetMutation();

  const saveSnapshotToDataset = async ({
    conversationId,
    timestamp,
    existingDatasetIds,
    newDatasets,
  }: SaveSnapshotArgs) => {
    const allDatasetIds = [...existingDatasetIds];

    if (newDatasets.length > 0) {
      const createResults = await Promise.all(
        newDatasets.map(({ name, environment }) =>
          createDatasetMutation({
            variables: {
              input: {
                name,
                description: undefined,
                environment,
              },
            },
          })
        )
      );

      for (const result of createResults) {
        if (
          !result.data?.createBraintrustDataset.success ||
          !result.data?.createBraintrustDataset.datasetId
        ) {
          throw new Error(
            result.data?.createBraintrustDataset.error ||
              'Failed to create dataset'
          );
        }
        allDatasetIds.push(result.data.createBraintrustDataset.datasetId);
      }
    }

    const result = await saveSnapshotMutation({
      variables: {
        input: {
          conversationId,
          timestamp: timestamp.toISOString(),
          datasetIds: allDatasetIds,
        },
      },
    });

    if (!result.data?.saveConversationSnapshotToDataset.success) {
      throw new Error(
        result.data?.saveConversationSnapshotToDataset.error ||
          'Failed to save snapshot to dataset'
      );
    }
  };

  return { saveSnapshotToDataset };
}
